import { useEffect, useRef, useState } from 'react';

/* KPI count-up (kpiT): cubic ease-out over 1100ms, snaps to final when
   motion is off / prefers-reduced-motion. */
export function useCountUp(target: number, motionOn = true, durationMs = 1100): number {
  const [value, setValue] = useState(motionOn ? 0 : target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!motionOn) {
      setValue(target);
      return;
    }
    const start = performance.now();
    const tick = (now: number): void => {
      const p = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3); // cubic ease-out
      setValue(target * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setValue(target);
    };
    rafRef.current = requestAnimationFrame(tick);
    const safety = window.setTimeout(() => setValue(target), 1400);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.clearTimeout(safety);
    };
  }, [target, motionOn, durationMs]);

  return value;
}

/** True when the OS requests reduced motion. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = (): void => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}
